import "./animatedButton.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

type AnimatedButtonEventName =
    | 'animated-button:click'
    | 'animated-button:saving'
    | 'animated-button:save-cancelled'
    | 'animated-button:default-action'
    | 'animated-button:saved'
    | 'animated-button:error'
    | 'animated-button:default';

type AnimatedButtonEventDetail = {
    buttonId: string;
    componentId: string;
    state: AnimatedButtonState;
    sourceEvent?: Event;
};

type AnimatedButtonHook = (
    event: Event,
    instance: AnimatedButton
) => boolean | void | Promise<boolean | void>;

type AnimatedButtonState = 'saving' | 'saved' | 'error' | 'default';

type AnimatedButtonOptions = {
    onClick?: AnimatedButtonHook;
    onSave?: AnimatedButtonHook;
    savingLabel?: string;
    savedLabel?: string;
    errorLabel?: string;
    defaultLabel?: string;
    savingBtnDelay?: number;
    savedBtnDelay?: number;
};

export class AnimatedButton {
    private static readonly registry = new ComponentRegistry<AnimatedButton>('animated-button');
    private static readonly states = {
        saving: 'saving',
        saved: 'saved',
        error: 'error',
        default: 'default',
    } as const;

    private static readonly defaultLabels = {
        saving: 'Saving',
        saved: 'Saved',
        error: 'Error',
        default: 'Save',
    };

    private readonly id: string;
    private readonly button: HTMLButtonElement | HTMLAnchorElement | HTMLInputElement;
    private readonly defaultText: string;
    private timeoutId: number | null = null;
    private disabled = false;

    private readonly handleClick = async (e: Event): Promise<void> => {
        if (this.disabled || this.button.dataset.state === AnimatedButton.states.saving) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        this.emit('animated-button:click', AnimatedButton.states.default, e);

        try {
            if (typeof this.options.onClick === 'function') {
                const shouldContinue = await this.options.onClick(e, this);
                if (shouldContinue === false) {
                    this.emit('animated-button:save-cancelled', AnimatedButton.states.default, e);
                    return;
                }
            }
            this.emit('animated-button:saving', AnimatedButton.states.saving, e);
            this.setState(AnimatedButton.states.saving, this.savingBtnDelay, async () => {               
                this.emit('animated-button:default-action', AnimatedButton.states.saving, e);
                await this.performDefaultAction();
                
                if (typeof this.options.onSave === 'function') {
                    await this.options.onSave(e, this);
                }
            });
        } catch (error) {
            this.emit('animated-button:error', AnimatedButton.states.error, e);
            this.setState(AnimatedButton.states.error);
        }
    };

    private emit(name: AnimatedButtonEventName, state: AnimatedButtonState, sourceEvent?: Event): void {
        this.button.dispatchEvent(
            new CustomEvent<AnimatedButtonEventDetail>(name, {
                bubbles: true,
                detail: {
                    buttonId: this.button.id || '',
                    componentId: this.id,
                    state,
                    sourceEvent
                }
            })
        );
    }

    private async performDefaultAction(): Promise<void> {
        if (this.button instanceof HTMLButtonElement || this.button instanceof HTMLInputElement) {
            if (this.button.type === 'submit') {
                const form = this.button.closest('form');
                if (form) {
                    form.requestSubmit(this.button);
                }
                return;
            }
        }
    }

    private readonly btnLabels = {
        saving: AnimatedButton.defaultLabels.saving,
        saved: AnimatedButton.defaultLabels.saved,
        error: AnimatedButton.defaultLabels.error,
        default: AnimatedButton.defaultLabels.default,
    };

    private savingBtnDelay = 750;
    private savedBtnDelay = 1500;

    // Private constructor - assumes validated button
    private constructor(
        button: HTMLButtonElement | HTMLAnchorElement | HTMLInputElement,
        private options: AnimatedButtonOptions = {}
    ) {
        this.id = AnimatedButton.registry.register(button, this);
        this.button = button;
        button.classList.add('animated-button-component');

        const savingLabel =
            this.options.savingLabel ??
            button.getAttribute('data-saving-label') ??
            this.btnLabels.saving;

        const savedLabel =
            this.options.savedLabel ??
            button.getAttribute('data-saved-label') ??
            this.btnLabels.saved;

        const errorLabel =
            this.options.errorLabel ??
            button.getAttribute('data-error-label') ??
            this.btnLabels.error;

        const defaultLabel =
            this.options.defaultLabel ??
            button.getAttribute('data-default-label') ??
            (button instanceof HTMLInputElement ? button.value : button.textContent?.trim()) ??
            this.btnLabels.default;

        this.defaultText = defaultLabel;

        this.btnLabels.saving = savingLabel;
        this.btnLabels.saved = savedLabel;
        this.btnLabels.error = errorLabel;
        this.btnLabels.default = defaultLabel;

        this.savingBtnDelay = this.options.savingBtnDelay ?? this.savingBtnDelay;
        this.savedBtnDelay = this.options.savedBtnDelay ?? this.savedBtnDelay;
        this.button.addEventListener('click', this.handleClick);
    }

    private setButtonText(text: string): void {
        if (this.button instanceof HTMLInputElement) {
            this.button.value = text;
            return;
        }

        this.button.textContent = text;
    }

    // Factory Method

    public static create(
        target: string | HTMLButtonElement | HTMLAnchorElement | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: AnimatedButtonOptions = {}
    ): AnimatedButton[] | null {
        const buttons = ComponentRegistry.processTargets<AnimatedButton, HTMLElement>(
            AnimatedButton.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement, HTMLInputElement]
        ) as (HTMLButtonElement | HTMLAnchorElement | HTMLInputElement)[];

        if (!buttons.length) return null;

        return buttons.map(button => new AnimatedButton(button, options));
    }

    public static getButton(id: string): AnimatedButton | null {
        return AnimatedButton.registry.get(id) ?? null;
    }

    public static getFormButton(form: HTMLFormElement): AnimatedButton | null {
        const submitButton = form.querySelector('.animated-button');
        if (!submitButton) return null;
        return AnimatedButton.getButton(submitButton.id);
    }

    // Public Instance Methods

    private setDisabled(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.button instanceof HTMLButtonElement || this.button instanceof HTMLInputElement) {
            this.button.disabled = isDisabled;
            return;
        }

        this.button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

        if (isDisabled) {
            this.button.tabIndex = -1;
            return;
        }

        this.button.removeAttribute('tabindex');
    }
    
    public setState = (state: AnimatedButtonState, delay?: number, callback?: () => void): void => {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        switch (state) {
            case AnimatedButton.states.saving:
                this.button.dataset.state = AnimatedButton.states.saving;
                this.setButtonText(this.btnLabels.saving);

                if (callback) {
                    this.timeoutId = window.setTimeout(() => {
                        void Promise.resolve(callback()).catch(() => {
                            this.setState(AnimatedButton.states.error);
                        });
                    }, delay ?? this.savingBtnDelay);
                }
                break;

            case AnimatedButton.states.saved:
                this.button.dataset.state = AnimatedButton.states.saved;
                this.setButtonText(this.btnLabels.saved);
                this.emit('animated-button:saved', AnimatedButton.states.saved);
                this.timeoutId = window.setTimeout(() => {
                void Promise.resolve(callback?.())
                    .catch(() => {})
                    .finally(() => {
                        this.setState(AnimatedButton.states.default);
                    });
                }, delay ?? this.savedBtnDelay);
                break;

            case AnimatedButton.states.error:
                this.button.dataset.state = AnimatedButton.states.error;
                this.setButtonText(this.btnLabels.error);
                this.emit('animated-button:error', AnimatedButton.states.error);

                this.timeoutId = window.setTimeout(() => {
                void Promise.resolve(callback?.())
                    .catch(() => {})
                    .finally(() => {
                        this.setState(AnimatedButton.states.default);
                    });
                }, delay ?? this.savedBtnDelay);
                break;

            default:
                delete this.button.dataset.state;
                this.setButtonText(this.defaultText);
                break;
        }
    }

    public setOnClick(onClick?: AnimatedButtonHook): void {
        this.options.onClick = onClick;
    }

    public setOnSave(onSave?: AnimatedButtonHook): void {
        this.options.onSave = onSave;
    }

    public disable(): void {
        this.setDisabled(true);
    }

    public enable(): void {
        this.setDisabled(false);
    }

    public destroy = (): void => {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        this.button.removeEventListener('click', this.handleClick);
        delete this.button.dataset.state;
        this.setButtonText(this.defaultText);
        AnimatedButton.registry.unregister(this.id);
    };
}